// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { AppConstants, LMDialogButtonKey } from '@utils/app.constants';
import { Observable } from 'rxjs';
import { LMUniqueIdService } from '@utils/index';
import { LMSelectDialogComponent } from '@modal/lm-select-dialog.component';
import { first } from 'rxjs/operators';
import { LMContextService, LMModalDialogWrapperRef, LMModalWrapperService } from '@app/services';

export interface LMSelectResponse {
  status: string
  rows?: Array<any>
  dataViews?: Array<LMDataView>
  multiSelectedValues?: Array<LMMultiSelectedValue>
}

export interface LMSelectDialogOptions {
  listUrlInfo: LMListUrlInfo;
  listSpec: LMListSpec;
  title: string;
  sourceField: string;
  className?: string;
  allowMultiSelections?: boolean;
  maintainSelectedValues?: boolean;
  selectRequestData?: SelectRequestData;
}

@Injectable()
export class LMSelectDialogService {
  constructor(
    private lmUniqueIdService: LMUniqueIdService,
    private lmContextService: LMContextService,
    private lmModalWrapperService: LMModalWrapperService,
  ) {}

  public showSelectListDialog(options: LMSelectDialogOptions): Observable<LMSelectResponse> {
    options.className = options.className || '';
    options.allowMultiSelections = options.selectRequestData?.allowMultiSelections || false;
    options.maintainSelectedValues = options.selectRequestData?.maintainSelectedValues || false;

    return this.openDialog(options);
  }

  private openDialog(options: LMSelectDialogOptions): Observable<LMSelectResponse> {
    return new Observable<LMSelectResponse>(observer => {
      let selectDialogComponent: LMSelectDialogComponent;
      const busListSpecName = options.listSpec.busListSpec.name;
      const buttons = [];

      // Create the Done Editing button
      buttons.push(
        {
          id: this.lmUniqueIdService.uniqueId([ busListSpecName, options.sourceField,  LMDialogButtonKey.DONEEDITING ]),
          text: Soho.Locale.translate(LMDialogButtonKey.DONEEDITING),
          click: ( ) => {
            if (selectDialogComponent) {
              selectDialogComponent.selectList.turnOnSelectEditMode(false);
            }
          }
        }
      );

      if (options.allowMultiSelections) {
        // Create the Cancel button
        buttons.push({
          id: this.lmUniqueIdService.uniqueId([ busListSpecName, options.sourceField, LMDialogButtonKey.CANCEL ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            modal.close(true);
          }
        });

        // Create the Ok button
        buttons.push({
          id: this.lmUniqueIdService.uniqueId([ busListSpecName, options.sourceField, LMDialogButtonKey.OK ]),
          text: Soho.Locale.translate(LMDialogButtonKey.OK),
          isDefault: true,
          click: () => {
            if (!selectDialogComponent.selectList.inSelectListEditMode) {
              this.handleSelection(dialogRef, selectDialogComponent.getSelectionEvent(), options.allowMultiSelections, options.maintainSelectedValues);
            }
          }
        });
      }

      const attributes: SohoAttributesArray = [ {
        name: 'data-automation-id',
        value: `lm-${options.listSpec?.busListSpec?.automationId}-modal`
      } ];

      const sohoModalOptions: SohoModalOptions = {
        fullsize: this.lmContextService.inMingleOverlay() ? 'always' : 'responsive',
        // fullsize: 'sizeToGrid'
        noRefocus: true,
        attributes
      };

      const dialogRef = this.lmModalWrapperService
        .modal(LMSelectDialogComponent)
        .options(sohoModalOptions)
        .buttons(buttons)
        .title(options.title)
        .isAlert(false)
        .showCloseBtn(true)
        .autoFocus(false)
        .cssClass(options.className)
        .beforeOpen(() => {
          if (options.allowMultiSelections) {
            const dialogButtons = dialogRef.buttonsetAPI.element.children;
            const doneEditingButton = dialogButtons[0] as HTMLElement;
            doneEditingButton.setAttribute('style', 'display:none; width:100%');

            const cancelButton = dialogButtons[1] as HTMLElement;
            cancelButton.style.width = '50%';

            const okButton = dialogButtons[2] as HTMLElement;
            okButton.style.width = '50%';
          } else {
            dialogRef.buttonsetAPI.element.style.display = 'none';
          }

          return true;
        })
        .open()
        .apply((dialogComponent: LMSelectDialogComponent) => {
          selectDialogComponent = dialogComponent;
          selectDialogComponent.listUrlInfo = options.listUrlInfo;
          selectDialogComponent.listSpec = options.listSpec;
          selectDialogComponent.selectRequestData = options.selectRequestData;

          selectDialogComponent.titleChanged.pipe(first()).subscribe(newTitle => {
            dialogRef.title(newTitle);
          });

          // need to listen for all occurrences as will get a row selection for a 'deselection'
          selectDialogComponent.selectionEvent.subscribe((selectionEvent: LMSelectionEvent) => {
            if (!selectDialogComponent.selectList.inSelectListEditMode && !options.allowMultiSelections) {
              setTimeout(() => {
                this.handleSelection(dialogRef, selectionEvent, options.allowMultiSelections, options.maintainSelectedValues);
              });
            }
          });

          selectDialogComponent.navigatingToolbarEvent.pipe(first()).subscribe(() => {
            dialogRef.close(<LMSelectResponse>{ status: AppConstants.STATUS_COMPLETED });
          });

          selectDialogComponent.triggerDetectChanges();
        })
        .afterClose(response => {
          if (!response) {
            response = { status: AppConstants.STATUS_CANCEL };
          }
          observer.next(response);
          observer.complete();
        });
    });
  }

  private handleSelection(dialogRef: LMModalDialogWrapperRef<LMSelectDialogComponent>, selectionEvent: LMSelectionEvent, allowMultiSelections: boolean, maintainSelectedValues: boolean) {
    const selectResponse: LMSelectResponse = { status: AppConstants.STATUS_COMPLETED };

    if (selectionEvent) {
      if (maintainSelectedValues) {
        selectResponse.dataViews = [];
        selectResponse.rows = selectionEvent.rows;
        selectResponse.multiSelectedValues = dialogRef.componentDialog.selectList.multiSelectedValues;
      } else if (allowMultiSelections) {
        selectResponse.rows = selectionEvent.rows;
        selectResponse.dataViews = selectionEvent.rows.map(row => row.data);
      } else {
        // The soho-lookup component requires a data object on the array element
        selectResponse.rows = [ { data: selectionEvent.dataView } ];
        selectResponse.dataViews = [ selectionEvent.dataView ];
      }
    }
    dialogRef.close(selectResponse);
  }
}

export class LMSelectionEvent {
  constructor(
    public eventSource: LMSelectDialogComponent,
    public rows: Array<any>,
    public dataView?: LMDataView) {}
}
